import type { ResourceType, Severity } from './types';

/**
 * Severity matrix: maps exposure_type + access_scope to a severity level.
 *
 * Based on the security posture evaluation rules:
 * - Management ports (SSH/RDP/etc) + public unrestricted → critical
 * - Management ports + public restricted → high
 * - Service ports + public unrestricted → high
 * - Service ports + public restricted → medium
 * - Insecure protocols + any → medium
 * - Partially exposed → low
 */
export const SEVERITY_MATRIX: Record<string, Record<string, Severity>> = {
  management_port: {
    public_unrestricted: 'critical',
    public_restricted: 'high',
    partially_exposed: 'low',
  },
  service_port: {
    public_unrestricted: 'high',
    public_restricted: 'medium',
    partially_exposed: 'low',
  },
  insecure_protocol: {
    public_unrestricted: 'medium',
    public_restricted: 'medium',
    partially_exposed: 'low',
  },
};

/**
 * Resource categories grouping resource types by domain.
 */
export const RESOURCE_CATEGORIES: Record<string, ResourceType[]> = {
  core: ['vpc', 'subnet', 'route_table', 'internet_gateway'],
  security: [
    'firewall_rule',
    'firewall_policy',
    'security_group',
    'network_acl',
    'cloud_armor_policy',
    'network_firewall',
    'waf_web_acl',
    'waf_rule_group',
  ],
  load_balancing: [
    'load_balancer',
    'target_group',
    'backend_service',
    'target_proxy',
    'url_map',
  ],
  vpn: ['vpn_connection', 'vpn_tunnel', 'vpn_gateway', 'customer_gateway'],
  transit: [
    'transit_gateway',
    'transit_gateway_attachment',
    'transit_gateway_route_table',
  ],
  interconnect: [
    'direct_connect_connection',
    'direct_connect_virtual_interface',
    'direct_connect_gateway',
    'partner_interconnect',
    'dedicated_interconnect',
  ],
  sdwan: [
    'cloud_wan_core_network',
    'global_network',
    'ncc_hub',
    'ncc_spoke',
    'network_manager_site',
    'network_manager_link',
    'network_manager_device',
  ],
  dns: ['dns_zone', 'dns_record', 'dns_policy', 'resolver_endpoint', 'resolver_rule'],
  nat: ['nat_gateway'],
  private_connectivity: [
    'psc_endpoint',
    'psc_service_attachment',
    'vpc_endpoint',
    'vpc_endpoint_service',
    'service_networking_connection',
  ],
  peering: ['peering', 'shared_vpc'],
  global_traffic: [
    'global_accelerator',
    'global_accelerator_listener',
    'global_accelerator_endpoint_group',
  ],
  cdn: ['cloudfront_distribution'],
  other: ['elastic_ip', 'network_interface', 'prefix_list', 'packet_mirroring', 'cloud_router'],
};

/**
 * Default cache TTL values in seconds for Workers KV.
 */
export const CACHE_TTL_DEFAULTS = {
  /** TTL for resource list/detail responses */
  resources: 300,
  /** TTL for security summary/findings */
  security: 120,
  /** TTL for metric data points */
  metrics: 300,
  /** TTL for dashboard summary */
  dashboard: 120,
  /** TTL for sync status */
  sync: 60,
  /** TTL for monitoring section endpoints (30s for high-frequency data) */
  monitoring: 30,
} as const;
